import { HttpClient, HttpHeaders } from '@angular/common/http'
import { inject, Injectable, OnDestroy } from '@angular/core'
import { UntypedFormArray } from '@angular/forms'
import { ServerRepresentedEvent, ServerRepresentedSingleEventRegistrationsCount } from '@app/modules/events/models/event.model'
import { BetaFeaturesService } from '@app/services/beta-features.service'
import { ToastrService } from '@app/services/toastr.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AuthService } from '@auth/services/auth.service'
import { RegistrationData } from '@database/models/registration-data.interface'
import { RegistrationsRequest } from '@database/models/registrations.request.interface'
import { RegistrationsResponse } from '@database/models/registrations.response.interface'
import { environment } from '@environments/environment'
import { NbDialogRef } from '@nebular/theme'
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs'
import { map, takeUntil, tap } from 'rxjs/operators'
import { ApprovalStatus } from '../models/registration-status.enum'
import { NotificationChannel } from '../models/resend-confirmation-pricing'
import { BulkProgressService } from './bulk-progress.service'

export type ResendConfirmationSingleChannel = NotificationChannel.EMAIL | NotificationChannel.SMS | NotificationChannel.WHATSAPP

/**
 * Defines a list of available order types
 */
export enum OrderTypes {
  DIFFERENCE = 'difference',
  VIRTUAL = 'virtual',
  PHYSICAL = 'physical',
  FREE = 'free'
}
/**
 * Defines the structure of an order request
 */
export interface Order {
  /** Product price id */
  price_id: string
  /** Order type */
  type: OrderTypes
  /** List of associated ids - used in case of registrations order */
  ids?: string[]
  /** Quantity of settled registrations - used in case of forms order */
  quantity?: number
}
/**
 * Defines a forms order interface
 */
export interface OrderByForms {
  /**
   * Forms array of orders
   */
  forms: {
    /** form id */
    id: number
    /** List of orders in that form id */
    orders: Order[]
  }[]
}
/** Defines a registrations order interface */
export interface OrderByRegistrations {
  /** Form id */
  form_id: number
  /** List of orders in that form id */
  orders: Order[]
}

/**
 * Handles database operations including exports
 */
@Injectable({
  providedIn: 'root'
})
export class DatabaseService implements OnDestroy {
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()
  private _seatingEnabled = new BehaviorSubject<boolean>(false)
  private _dataAlerts$ = new Subject<{ type: Alert; message: string }>()
  public readonly seatingEnabled$ = this._seatingEnabled.asObservable()
  private readonly betaFeaturesService = inject(BetaFeaturesService)
  private _currentEvent: ServerRepresentedEvent

  public get currentEvent(): ServerRepresentedEvent {
    return this._currentEvent
  }
  public set currentEvent(value: ServerRepresentedEvent) {
    this._currentEvent = value
  }

  private _email

  set email(value) {
    this._email = value
  }
  get email() {
    return this._email
  }

  public get dataAlerts$() {
    return this._dataAlerts$.asObservable()
  }

  /** Event's list of form */
  private _forms: UntypedFormArray

  /**
   * Dialog reference
   */
  private dialogRef: NbDialogRef<any>

  /** On destroy implementation */
  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  updateSeatingEnabled(value: boolean): void {
    this._seatingEnabled.next(value)
  }

  toastrService: ToastrService
  /**
   * Creates the service and injects it's dependencies
   * @param http HttpClient
   * @param auth AuthService
   * @param bulkProgressService BulkProgressService
   */
  constructor(private http: HttpClient, private auth: AuthService, private _ToastrService: ToastrService, private bulkProgressService: BulkProgressService) {
    this.toastrService = _ToastrService
  }

  /**
   * Shows a data alert
   * @param {Alert} type
   * @param {string} message
   */
  public alert(type: Alert, message: string) {
    this._dataAlerts$.next({ type, message })
  }

  /**
   * Builds the query-string part used to filter/search registrations.
   * This is reused both for the listing endpoint and for bulk actions payloads
   * so that the backend can replay the exact same filters for queued operations.
   * @param registrationsRetrievalRequest registration request
   * @param forBulkAction when true, strips scroll-pagination params (page/sort_key) so bulk replay uses full filtered set
   * @returns serialized query-string without leading '?'
   */
  public buildRegistrationsSearchQuery(registrationsRetrievalRequest: RegistrationsRequest, forBulkAction = false): string {
    const page = forBulkAction ? 1 : registrationsRetrievalRequest.page
    // Base pagination + basic filters
    const keyword = registrationsRetrievalRequest.searchTerm ? encodeURIComponent(registrationsRetrievalRequest.searchTerm) : ''
    const approved = registrationsRetrievalRequest.approved ? encodeURIComponent(registrationsRetrievalRequest.approved) : ''
    const flagged = registrationsRetrievalRequest.flagged ? encodeURIComponent(registrationsRetrievalRequest.flagged) : ''
    const refunded = registrationsRetrievalRequest.refunded ? encodeURIComponent(registrationsRetrievalRequest.refunded) : ''

    let query =
      `perpage=${registrationsRetrievalRequest.perPage}&page=${page}&keyword=${keyword}` +
      `&filters[approved]=${approved}&filters[flagged]=${flagged}&filters[refunded]=${refunded}`

    // Day / attendance filters - handle both attendedDays and missedDays separately
    if (registrationsRetrievalRequest.attendedDays && registrationsRetrievalRequest.attendedDays.length > 0) {
      const queryString = registrationsRetrievalRequest.attendedDays.map((item) => `filters[attended_days][]=${encodeURIComponent(item)}`).join('&')
      query = query + `&${queryString}`
    }

    if (registrationsRetrievalRequest.missedDays && registrationsRetrievalRequest.missedDays.length > 0) {
      const queryString = registrationsRetrievalRequest.missedDays.map((item) => `filters[missed_days][]=${encodeURIComponent(item)}`).join('&')
      query = query + `&${queryString}`
    }

    // Fallback to legacy days/attended format if new format not available
    if (
      !registrationsRetrievalRequest.attendedDays &&
      !registrationsRetrievalRequest.missedDays &&
      registrationsRetrievalRequest.days &&
      registrationsRetrievalRequest.days.length &&
      registrationsRetrievalRequest.attended
    ) {
      if (registrationsRetrievalRequest.attended == 'yes') {
        const queryString = registrationsRetrievalRequest.days.map((item) => `filters[attended_days][]=${encodeURIComponent(item)}`).join('&')
        query = query + `&${queryString}`
      } else {
        const queryString = registrationsRetrievalRequest.days.map((item) => `filters[missed_days][]=${encodeURIComponent(item)}`).join('&')
        query = query + `&${queryString}`
      }
    }

    // Dynamic field filters (column filters)
    if (registrationsRetrievalRequest.fieldFilters) {
      Object.entries(registrationsRetrievalRequest.fieldFilters).forEach(([field, filterConfig]) => {
        // Check if it's the new format { values: [...], inverted?: boolean } or old format [...]
        const isNewFormat = filterConfig && !Array.isArray(filterConfig) && 'values' in filterConfig
        const values = isNewFormat ? (filterConfig as any).values : filterConfig
        const inverted = isNewFormat ? (filterConfig as any).inverted : false

        if (values && values.length > 0) {
          if (field === '#email') {
            values.forEach((value: string) => {
              const queryValues = value === 'Sent' ? ['pending', 'Sent'] : [value]
              queryValues.forEach((queryValue) => {
                query = query + `&filters[mail_status][]=${encodeURIComponent(queryValue)}`
              })
            })
          } else if (field === '#whatsapp') {
            values.forEach((value: string) => {
              query = query + `&filters[whatsapp_status][]=${encodeURIComponent(value)}`
            })
          } else {
            values.forEach((value: string) => {
              query = query + `&field_filters[${encodeURIComponent(field)}][values][]=${encodeURIComponent(value)}`
            })
            if (inverted) {
              query = query + `&field_filters[${encodeURIComponent(field)}][inverted]=1`
            }
          }
        }
      })
    }

    // Sort parameters
    if (registrationsRetrievalRequest.sortField) {
      query = query + `&sort_field=${encodeURIComponent(registrationsRetrievalRequest.sortField)}`
      query = query + `&sort_direction=${registrationsRetrievalRequest.sortDirection || 'desc'}`
    }

    // Keyset cursor for the cursor-mode fast-path. Only sent on
    // load-more requests (not initial loads or filter/sort changes), so
    // the backend can skip skip()+limit() AND the countDocuments() that
    // paginate() implies. Never include in bulk action search replay.
    if (!forBulkAction && registrationsRetrievalRequest.sortKey) {
      query = query + `&sort_key=${encodeURIComponent(registrationsRetrievalRequest.sortKey)}`
    }

    return query
  }

  /** Search query for bulk ops — filters/sort only, no scroll pagination cursor. */
  public buildBulkRegistrationsSearchQuery(registrationsRetrievalRequest: RegistrationsRequest): string {
    return this.buildRegistrationsSearchQuery(registrationsRetrievalRequest, true)
  }

  /**
   * Gets a list of registrations in an event based on the registration request criterion
   * @param registrationsRetrievalRequest registration request
   * @returns RegistrationsResponse
   */
  getEventRegData(registrationsRetrievalRequest: RegistrationsRequest) {
    const baseUrl = `${environment.baseContactlessApi}v3/query/search/${registrationsRetrievalRequest.workspace}/${registrationsRetrievalRequest.event}/${registrationsRetrievalRequest.form}`
    const query = this.buildRegistrationsSearchQuery(registrationsRetrievalRequest)
    const url = `${baseUrl}?${query}`

    return this.http.get<RegistrationsResponse>(url).pipe(takeUntil(this._destroy$))
  }

  /**
   * Gets an average of all rating fields for every persona
   * @param workspaceSlug workspace slug
   * @param eventSlug Event slug
   * @param formSlug Form slug
   * @returns Observable
   */
  getAvgRatingData(workspaceSlug: string, eventSlug: string, formSlug: string): Observable<any> {
    return this.http.get<any>(`${environment.planningDashboard}star-rating/${workspaceSlug}/${eventSlug}/${formSlug}`)
  }

  /**
   * Returns a forms property in an object that defines each form registration counts for requested forms
   * @param eventSlug Event slug
   * @param formSlugs Form slugs
   * @returns forms property that contains each form registrations count
   */
  getEventFormCounts(eventSlug, formSlugs: string[]) {
    return this.http.post<any>(`${environment.baseContactlessApi}v3/registrations/count/${this.auth.currentWorkspace.slug}/${eventSlug}`, {
      forms: formSlugs
    })
  }
  /**
   * Approves a registration
   * @param eventSlug Event slug
   * @param personaSlug Persona slug
   * @param registrationId Registration id
   * @param approval Whether it's approved or not
   * @returns Observable
   */
  approveRegistration(eventSlug: string, personaSlug: string, registrationId: string, status: ApprovalStatus): Observable<any> {
    const url =
      status == ApprovalStatus.AWAITING_REVISION ? 'request-revision' : status == ApprovalStatus.APPROVED || status == ApprovalStatus.PENDING_PAYMENT ? 'approve' : 'decline'

    const payload =
      url === 'request-revision'
        ? {
            subject: this._email.subject,
            message: this._email.message,
            registrationButtonContent: this._email.registrationButtonContent
          }
        : {}
    return this.http
      .patch(`${environment.registrationsApi}${url}/${this.auth.currentWorkspace.slug}/${eventSlug}/${personaSlug}/${registrationId}`, payload)
      .pipe(takeUntil(this._destroy$))
  }

  /**
   * Attends or unattends a registration on a specific zone slug
   * @param eventSlug Event slug
   * @param spotSlug Zone slug
   * @param registrationId registration id
   * @param attended Whether it's attended or not
   * @returns Observable
   */
  attendRegistration(eventSlug: string, spotSlug: string, registrationId: string, attended: boolean): Observable<any> {
    return this.http
      .patch(
        `${environment.registrationsApi}check/${attended ? 'attend' : 'unattend'}/${this.auth.currentWorkspace.slug}/${eventSlug}/${
          attended ? spotSlug + '/' : ''
        }${registrationId}`,
        {}
      )
      .pipe(takeUntil(this._destroy$))
  }
  /**
   * Flags/ Unflags a registration
   * @param eventSlug event slug
   * @param personaSlug persona slug
   * @param registrationId registration id
   * @param flagged whether it's flagged or not
   * @returns Observable
   */
  toggleFlag(eventSlug: string, personaSlug: string, registrationId: string, flagged: boolean) {
    return this.http
      .patch(`${environment.registrationsApi}${flagged ? 'flag' : 'unflag'}/${this.auth.currentWorkspace.slug}/${eventSlug}/${personaSlug}/${registrationId}`, {})
      .pipe(takeUntil(this._destroy$))
  }
  /**
   * Updates flag with color and comment in third_parts
   * Uses the new dedicated endpoint process/update/flags
   * @param eventSlug event slug
   * @param personaSlug persona slug (form slug) - not used but kept for consistency
   * @param registrationId registration id
   * @param flags the flags array to set in third_parts
   * @returns Observable
   */
  updateFlags(eventSlug: string, personaSlug: string, registrationId: string, flags: any[], unflag: boolean = false) {
    return this.http
      .patch(`${environment.registrationsApi}process/update/flags`, {
        workspace_slug: this.auth.currentWorkspace.slug,
        event_slug: eventSlug,
        id: registrationId,
        flags: flags,
        unflag: unflag
      })
      .pipe(takeUntil(this._destroy$))
  }
  /**
   * Gets a single registration details
   * @param eventSlug event slug
   * @param personaSlug persona slug
   * @param registrationId registration id
   * @returns Observable
   */
  getRegistration(eventSlug: string, personaSlug: string, registrationId: string): Observable<RegistrationData> {
    return this.http
      .get<{ document: RegistrationData }>(`${environment.registrationsApi}process/confirmation/${this.auth.currentWorkspace.slug}/${eventSlug}/${personaSlug}/${registrationId}`)
      .pipe(
        map((response: { document: RegistrationData }) => {
          return response.document
        }),
        takeUntil(this._destroy$)
      )
  }
  /**
   * Deletes a registration
   * @param eventSlug event slug
   * @param personaSlug persona slug
   * @param registrationId registration id
   * @returns Observable
   */
  deleteRegistration(eventSlug: string, personaSlug: string, registrationId: string) {
    return this.http
      .delete(`${environment.registrationsApi}delete/${this.auth.currentWorkspace.slug}/${eventSlug}/${personaSlug}/${registrationId}`)
      .pipe(takeUntil(this._destroy$))
  }
  /**
   * Deletes multiple registrations
   * @param eventSlug event slug
   * @param personaSlug persona slug
   * @param registrationIds target ids
   * @param action bulk operation action
   * @param count number of registrations
   * @param parent_id parent registration id when acting on children in "exclude" mode
   * @param search serialized search / filter query used for the listing (optional)
   * @returns Observable
   */
  bulkDeleteRegistrations(eventSlug: string, personaSlug: string, registrationIds: string[], action: string, count: number, parent_id?: string, search?: string) {
    console.log('🚀 bulkDeleteRegistrations called', { count, eventName: this.currentEvent?.name })

    // Show toast IMMEDIATELY before making the request
    const tempId = `temp-${Date.now()}-${Math.random()}`
    this.bulkProgressService.addOperation(tempId, 'delete', count, this.currentEvent?.name || 'Event')

    return this.http
      .post<{ batch_id: string; status: string; total: number; result: any }>(
        `${environment.registrationsApi}delete-bulk/${this.auth.currentWorkspace.slug}/${eventSlug}/${personaSlug}`,
        {
          ids: registrationIds,
          action: action,
          count: count,
          parent_id: action === 'exclude' ? parent_id : undefined,
          search: search
        }
      )
      .pipe(
        tap((response) => {
          console.log('🎯 Response received:', response)
          if (response.batch_id) {
            console.log('📊 Updating operation with real batch_id', response.batch_id)
            // Update the temp operation with the real batch_id
            this.bulkProgressService.updateOperationId(tempId, response.batch_id)
            this.bulkProgressService.startPolling(response.batch_id)
          } else {
            console.warn('⚠️ No batch_id in response!')
            // Remove the temp operation if no batch_id
            this.bulkProgressService.dismissOperation(tempId)
          }
        }),
        takeUntil(this._destroy$)
      )
  }
  /**
   * Approves/disapproves multiple registrations
   * @param eventSlug event slug
   * @param personaSlug persona slug
   * @param registrationIds target ids
   * @param status registration entry_status
   * @param action bulk operation action
   * @param count number of registrations
   * @param parent_id parent registration id when acting on children in "exclude" mode
   * @param search serialized search / filter query used for the listing (optional)
   * @returns Observable
   */
  bulkApproveRegistrations(
    eventSlug: string,
    personaSlug: string,
    registrationIds: string[],
    status: ApprovalStatus,
    action: string,
    count: number,
    parent_id?: string,
    search?: string
  ) {
    const url =
      status == ApprovalStatus.AWAITING_REVISION ? 'request-revision' : status == ApprovalStatus.APPROVED || status == ApprovalStatus.PENDING_PAYMENT ? 'approve' : 'decline'

    const payload =
      url === 'request-revision'
        ? {
            subject: this._email.subject,
            message: this._email.message,
            ids: registrationIds,
            action: action,
            count: count,
            registrationButtonContent: this._email.registrationButtonContent || 'Edit Registration',
            parent_id: action === 'exclude' ? parent_id : undefined,
            search: search
          }
        : {
            ids: registrationIds,
            action: action,
            count: count,
            parent_id: action === 'exclude' ? parent_id : undefined,
            search: search
          }

    const operationName = url === 'request-revision' ? 'revision' : status == ApprovalStatus.APPROVED || status == ApprovalStatus.PENDING_PAYMENT ? 'approve' : 'decline'

    // Show toast IMMEDIATELY before making the request
    const tempId = `temp-${Date.now()}-${Math.random()}`
    this.bulkProgressService.addOperation(tempId, operationName, count, this.currentEvent?.name || 'Event')

    return this.http
      .patch<{
        batch_id: string
        status: string
        total: number
        result: any
      }>(`${environment.registrationsApi}${url}-bulk/${this.auth.currentWorkspace.slug}/${eventSlug}/${personaSlug}`, payload)
      .pipe(
        tap((response) => {
          if (response.batch_id) {
            this.bulkProgressService.updateOperationId(tempId, response.batch_id)
            this.bulkProgressService.startPolling(response.batch_id)
          } else {
            this.bulkProgressService.dismissOperation(tempId)
          }
        }),
        takeUntil(this._destroy$)
      )
  }

  /**
   * Mark Attended multiple registrations
   * @param eventSlug event slug
   * @param personaSlug form slug
   * @param registrationIds target ids
   * @param spot event global spot
   * @param action bulk operation action
   * @param count number of registrations
   * @param parent_id parent registration id when acting on children in "exclude" mode
   * @param search serialized search / filter query used for the listing (optional)
   * @returns
   */
  bulkAttended(eventSlug: string, personaSlug: string, registrationIds: string[], spot: string, action: string, count: number, parent_id?: string, search?: string) {
    // Show toast IMMEDIATELY before making the request
    const tempId = `temp-${Date.now()}-${Math.random()}`
    this.bulkProgressService.addOperation(tempId, 'attend', count, this.currentEvent?.name || 'Event')

    return this.http
      .patch<{ batch_id: string; status: string; total: number; result: any }>(
        `${environment.registrationsApi}check/attend-bulk/${this.auth.currentWorkspace.slug}/${eventSlug}/${spot}`,
        {
          ids: registrationIds,
          action: action,
          form_slug: personaSlug,
          count: count,
          parent_id: action === 'exclude' ? parent_id : undefined,
          search: search
        }
      )
      .pipe(
        tap((response) => {
          if (response.batch_id) {
            this.bulkProgressService.updateOperationId(tempId, response.batch_id)
            this.bulkProgressService.startPolling(response.batch_id)
          } else {
            this.bulkProgressService.dismissOperation(tempId)
          }
        }),
        takeUntil(this._destroy$)
      )
  }
  /**
   * Mark Unattended multiple registrations
   * @param eventSlug event slug
   * @param personaSlug form slug
   * @param registrationIds target ids
   * @param action bulk operation action
   * @param count number of registrations
   * @param parent_id parent registration id when acting on children in "exclude" mode
   * @param search serialized search / filter query used for the listing (optional)
   * @returns
   */
  bulkUnattend(eventSlug: string, personaSlug: string, registrationIds: string[], action: string, count: number, parent_id?: string, search?: string) {
    // Show toast IMMEDIATELY before making the request
    const tempId = `temp-${Date.now()}-${Math.random()}`
    this.bulkProgressService.addOperation(tempId, 'unattend', count, this.currentEvent?.name || 'Event')

    return this.http
      .patch<{ batch_id: string; status: string; total: number; result: any }>(
        `${environment.registrationsApi}check/unattend-bulk/${this.auth.currentWorkspace.slug}/${eventSlug}`,
        {
          ids: registrationIds,
          action: action,
          form_slug: personaSlug,
          count: count,
          parent_id: action === 'exclude' ? parent_id : undefined,
          search: search
        }
      )
      .pipe(
        tap((response) => {
          if (response.batch_id) {
            this.bulkProgressService.updateOperationId(tempId, response.batch_id)
            this.bulkProgressService.startPolling(response.batch_id)
          } else {
            this.bulkProgressService.dismissOperation(tempId)
          }
        }),
        takeUntil(this._destroy$)
      )
  }
  /**
   * Flags/unflags multiple registrations
   * @param eventSlug event slug
   * @param personaSlug persona slug
   * @param registrationIds target ids
   * @param flagged whether they are flagged or not
   * @param action bulk operation action
   * @param count number of registrations
   * @param search serialized search / filter query used for the listing (optional)
   * @returns Observable
   */
  bulkFlagRegistrations(eventSlug: string, personaSlug: string, registrationIds: string[], flagged: boolean, action: string, count: number, search?: string) {
    const operationName = flagged ? 'flag' : 'unflag'

    // Show toast IMMEDIATELY before making the request
    const tempId = `temp-${Date.now()}-${Math.random()}`
    this.bulkProgressService.addOperation(tempId, operationName, count, this.currentEvent?.name || 'Event')

    return this.http
      .patch<{ batch_id: string; status: string; total: number; result: any }>(
        `${environment.registrationsApi}${flagged ? 'flag' : 'unflag'}-bulk/${this.auth.currentWorkspace.slug}/${eventSlug}/${personaSlug}`,
        {
          ids: registrationIds,
          action: action,
          count: count,
          search: search
        }
      )
      .pipe(
        tap((response) => {
          if (response.batch_id) {
            this.bulkProgressService.updateOperationId(tempId, response.batch_id)
            this.bulkProgressService.startPolling(response.batch_id)
          } else {
            this.bulkProgressService.dismissOperation(tempId)
          }
        }),
        takeUntil(this._destroy$)
      )
  }
  /**
   * Unhides a single field of an unsettled registration
   * @param fieldName field name
   * @param eventSlug event slug
   * @param formSlug persona slug
   * @param barcode registration barcode
   * @returns Observable
   */
  unhideField(fieldName: string, eventSlug: string, formSlug: string, barcode: string) {
    return this.http.get<{ [key: string]: string | string[] }>(
      `${environment.baseContactlessApi}v3/registrations/get/${this.auth.currentWorkspace.slug}/${eventSlug}/${formSlug}/${barcode}?field=fields.${fieldName}`
    )
  }

  /**
   * Resends confirmation to a registration via Email, SMS, or WhatsApp.
   * WhatsApp uses the bulk endpoint (`resend-wa-bulk`) with `action: include` and a single id.
   */
  resendConfirmation(
    eventSlug: string,
    registrationId: string,
    channel: ResendConfirmationSingleChannel = NotificationChannel.EMAIL,
    whatsappBulkContext?: {
      formSlug: string
      search?: string
    }
  ) {
    const workspaceSlug = this.auth.currentWorkspace.slug
    switch (channel) {
      case NotificationChannel.SMS:
        return this.http.patch(`${environment.registrationsApi}resend-sms/${workspaceSlug}/${eventSlug}/${registrationId}`, {})
      case NotificationChannel.WHATSAPP: {
        const formSlug = whatsappBulkContext?.formSlug?.trim()
        if (!formSlug) {
          return throwError(() => new Error('WhatsApp resend requires persona (form) slug'))
        }
        return this.bulkResendConfirmation(eventSlug, 'include', formSlug, [registrationId], 1, undefined, whatsappBulkContext.search, NotificationChannel.WHATSAPP)
      }
      default:
        return this.http.patch(`${environment.registrationsApi}resend-mail/${workspaceSlug}/${eventSlug}/${registrationId}`, {})
    }
  }

  /**
   * Resends confirmation to selected registrations (bulk).
   * @param eventSlug event slug
   * @param action action
   * @param personaSlug persona slug
   * @param ids registration ids
   * @param count number of registrations
   * @param parent_id parent registration id when acting on children in "exclude" mode
   * @param search serialized search / filter query used for the listing (optional)
   * @param channel email, sms, or whatsapp
   * @returns Observable
   */
  bulkResendConfirmation(
    eventSlug: string,
    action: string,
    personaSlug: string,
    ids: string[],
    count: number,
    parent_id?: string,
    search?: string,
    channel: NotificationChannel = NotificationChannel.EMAIL
  ) {
    const workspaceSlug = this.auth.currentWorkspace.slug
    let bulkUrl: string
    switch (channel) {
      case NotificationChannel.SMS:
        bulkUrl = `${environment.registrationsApi}resend-sms-bulk/${workspaceSlug}/${eventSlug}`
        break
      case NotificationChannel.WHATSAPP:
        bulkUrl = `${environment.registrationsApi}resend-wa-bulk/${workspaceSlug}/${eventSlug}`
        break
      default:
        bulkUrl = `${environment.registrationsApi}resend-mail-bulk/${workspaceSlug}/${eventSlug}`
    }

    // Show toast IMMEDIATELY before making the request
    const tempId = `temp-${Date.now()}-${Math.random()}`
    this.bulkProgressService.addOperation(tempId, 'resend', count, this.currentEvent?.name || 'Event')

    return this.http
      .patch<{ batch_id?: string; status?: string; total?: number; event_name?: string }>(bulkUrl, {
        action: action,
        ids: ids,
        form_slug: personaSlug,
        count: count,
        parent_id: action === 'exclude' ? parent_id : undefined,
        search: search
      })
      .pipe(
        tap((response) => {
          if (response.batch_id) {
            // Update the operation with the real batch_id AND event_name from API
            this.bulkProgressService.updateOperationId(tempId, response.batch_id)
            // Update event name from API response (it's in snake_case: event_name)
            if (response.event_name) {
              const operations = this.bulkProgressService['_operations$'].value
              const operation = operations.find((op) => op.batchId === response.batch_id)
              if (operation) {
                operation.eventName = response.event_name
                this.bulkProgressService['_operations$'].next([...operations])
              }
            }
            this.bulkProgressService.startPolling(response.batch_id)
          } else {
            this.bulkProgressService.dismissOperation(tempId)
          }
        }),
        takeUntil(this._destroy$)
      )
  }

  // Export services implementation

  /**
   * Settles a list of registrations within a prticular persona
   * @param order OrderByRegistrations
   * @returns Observable
   */
  settleByRegistrations(order: OrderByRegistrations) {
    return this.http.post(`${environment.baseContactlessApi}v3/plan/contactless/transactions/credit/registration`, order)
  }

  /**
   * Settles a list of forms and a quantity of registrations within a particular workspace
   * @param order OrderByForms
   * @returns Observable
   */
  settleByForms(order: OrderByForms) {
    return this.http.post(`${environment.baseContactlessApi}v3/plan/contactless/transactions/credit/form`, order)
  }

  /**
   * Returns the exportable counts for a single event in the current workspace
   * Uses caching to prevent duplicate API calls within the same request cycle
   * @param eventSlug event slug
   * @returns Observable<ServerRepresentedSingleEventRegistrationsCount>
   */
  public getExportableCountsForEvent(eventSlug: string): Observable<ServerRepresentedSingleEventRegistrationsCount> {
    return this.http.get<ServerRepresentedSingleEventRegistrationsCount>(`${environment.registrationsApi}count/${this.auth.currentWorkspace.slug}/${eventSlug}`)
  }

  /**
   * Uploads registrations in the import file to the form
   * @param eventSlug Event slug
   * @param formSlug Form slug
   * @param payload Request payload
   * @returns Observable
   */
  public uploadImportFile(eventSlug: string, formSlug: string, payload: any) {
    const formData = new FormData()
    formData.append('file', payload.file, payload.file.name)
    formData.append('send_email', (+payload.send_email).toString())
    formData.append('send_whatsapp', (+payload.send_whatsapp).toString())
    formData.append('send_sms', (+payload.send_sms).toString())
    formData.append('data_mapping', JSON.stringify(payload.data_mapping))
    formData.append('force_approval', '1')
    formData.append('check_email', (+(payload.check_email ?? 1)).toString())
    formData.append('send_upload_email', '1')

    // Build URL with fast_import parameter if beta feature is enabled
    let url = `${environment.registrationsApi}upload/${this.auth.currentWorkspace.slug}/${eventSlug}/${formSlug}`
    if (this.betaFeaturesService.isFeatureEnabled('optimized_import')) {
      url += '?fast_import=1'
    }

    return this.http.post(url, formData, {
      headers: new HttpHeaders({
        Accept: 'text/csv'
      })
    })
  }

  /**
   * Search parent (group) registrations for member import.
   * Drops rows that already belong to a parent.
   */
  public searchParentRegistrations(eventSlug: string, parentFormSlug: string, keyword: string, page = 1, perPage = 20): Observable<ParentRegistrationSearch> {
    const workspace = this.getWorkspaceSlug()
    const params = `keyword=${encodeURIComponent(keyword || '')}&perpage=${perPage}&page=${page}`
    return this.http.get(`${environment.registrationsApi}search/${workspace}/${eventSlug}/${parentFormSlug}?${params}`).pipe(
      map((body) => {
        const parsed = this.parseParentRegistrations(body)
        return {
          ...parsed,
          items: parsed.items.filter((row) => !row.parentId)
        }
      }),
      takeUntil(this._destroy$)
    )
  }

  /**
   * Load one registration and keep it only when it is a group parent.
   */
  public getParentRegistration(eventSlug: string, parentFormSlug: string, parentId: string): Observable<ParentRegistrationOption | null> {
    const workspace = this.getWorkspaceSlug()
    return this.http.get(`${environment.registrationsApi}process/get/${workspace}/${eventSlug}/${parentFormSlug}/${parentId}`).pipe(
      map((body) => {
        const row = this.parseParentRegistrations(body).items[0]
        if (!row || row.parentId) return null
        return row
      }),
      takeUntil(this._destroy$)
    )
  }

  /**
   * Create child registrations under a parent via import-members.
   * Do not set Content-Type; the browser must set the multipart boundary.
   */
  public importGroupMembers(eventSlug: string, parentFormSlug: string, payload: GroupMemberImportPayload): Observable<any> {
    const formData = new FormData()
    formData.append('file', payload.file, payload.file.name)
    formData.append('parent_id', payload.parentId)
    formData.append('data_mapping', JSON.stringify(payload.dataMapping))
    formData.append('force_approval', '1')
    formData.append('check_email', payload.checkEmail ? '1' : '0')
    formData.append('send_email', '0')
    formData.append('send_whatsapp', '0')
    formData.append('send_sms', '0')
    formData.append('send_upload_email', '1')

    const workspace = this.getWorkspaceSlug()
    return this.http
      .post(`${environment.registrationsApi}process/import-members/${workspace}/${eventSlug}/${parentFormSlug}`, formData, {
        headers: new HttpHeaders({
          Accept: 'application/json'
        })
      })
      .pipe(takeUntil(this._destroy$))
  }

  /**
   * Poll the in-flight member import for a parent registration.
   */
  public getMemberImportStatus(eventSlug: string, parentFormSlug: string, parentId: string): Observable<any> {
    const workspace = this.getWorkspaceSlug()
    return this.http.get(`${environment.registrationsApi}process/import-members/${workspace}/${eventSlug}/${parentFormSlug}/${parentId}`).pipe(takeUntil(this._destroy$))
  }

  /** Normalizes a search or get response into parent rows plus pagination. */
  private parseParentRegistrations(body: any): ParentRegistrationSearch {
    const list = this.unwrapRegistrationList(body)
    const items = list.map((item) => this.toParentRegistration(item)).filter((row): row is ParentRegistrationOption => !!row)
    const total = Number(body?.total ?? body?.pagination?.total ?? items.length)
    const perPage = Number(body?.per_page ?? body?.pagination?.per_page ?? 20)
    const currentPage = Number(body?.current_page ?? body?.pagination?.current_page ?? body?.page ?? 1)
    let lastPage = Number(body?.last_page ?? body?.pagination?.last_page ?? 0)
    // Laravel-style payloads may omit last_page; derive it from total + per_page.
    if ((!Number.isFinite(lastPage) || lastPage <= 0) && Number.isFinite(total) && total > 0 && Number.isFinite(perPage) && perPage > 0) {
      lastPage = Math.ceil(total / perPage)
    }
    return {
      items,
      total: Number.isFinite(total) ? total : items.length,
      currentPage: Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1,
      lastPage: Number.isFinite(lastPage) && lastPage > 0 ? lastPage : 0
    }
  }

  /** Pulls the registration list out of the shapes the search API returns. */
  private unwrapRegistrationList(body: any): any[] {
    if (!body) return []
    if (Array.isArray(body)) return body
    if (body.document) return [body.document]
    if (Array.isArray(body.data)) return body.data
    if (Array.isArray(body.data?.data)) return body.data.data
    if (Array.isArray(body.documents)) return body.documents
    if (Array.isArray(body.hits)) return body.hits
    if (body.id || body._id) return [body]
    return []
  }

  /** Maps one registration document to a parent option, including child count. */
  private toParentRegistration(item: any): ParentRegistrationOption | null {
    const doc = item?.document ?? item
    const id = doc?.id ?? doc?._id
    if (!id) return null
    const fields = doc.fields || {}
    const name = this.firstStringField(fields, ['fullname', 'name', 'first_name'])
    const email = this.firstStringField(fields, ['email'])
    const organization = this.firstStringField(fields, ['organization', 'company'])
    return {
      id: String(id),
      name,
      email,
      organization,
      childCount: Number(doc.child_count ?? 0) || 0,
      parentId: doc.parent_id ? String(doc.parent_id) : ''
    }
  }

  /** First non-empty string among the given registration fields. */
  private firstStringField(fields: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = fields[key]
      if (typeof value === 'string' && value.trim()) return value
    }
    return ''
  }

  /**
   * Setter for the _forms property
   */
  public set forms(forms: UntypedFormArray) {
    this._forms = forms
  }

  /**
   * Getter for the _forms property
   */
  public get forms() {
    return this._forms
  }

  /**
   * Rotate image in media library
   * @param workspaceSlug string
   * @param eventSlug string
   * @param formSlug string
   * @param element string
   * @param registration string
   * @param angle number
   * @returns Observable
   */
  public rotateImage(workspaceSlug: string, eventSlug: string, formSlug: string, element: string, registration: string, angle: number) {
    const payload = {
      workspace: workspaceSlug,
      event: eventSlug,
      form: formSlug,
      element: element,
      registration_id: registration,
      angle: angle
    }
    return this.http.patch(`${environment.mediaLibrary}public/rotate`, payload)
  }
  /**
   * Sets the dialog reference
   * @param ref NbDialogRef<any>
   * @returns void
   */
  setDialogRef(ref: NbDialogRef<any>): void {
    this.dialogRef = ref
  }

  /**
   * Closes the dialog that has been opened
   * @returns void
   */
  closeDialog(): void {
    this.dialogRef.close()
  }

  /**
   * @method Resets counter for event
   * @returns Observable<string>
   */
  public resetCounter(eventSlug: string): Observable<string> {
    return this.http.post<string>(`${environment.registrationsApi}count/reset/${this.auth.currentWorkspace.slug}/${eventSlug}`, {})
  }

  /**
   * refund registration
   */
  public refund(eventSlug: string, personaSlug: string, registrationId: string, amount: number, decline: boolean) {
    return this.http.post(`${environment.registrationsApi}refund_payment/${this.auth.currentWorkspace.slug}/${eventSlug}/${personaSlug}/${registrationId}`, {
      amount,
      decline
    })
  }

  /**
   * Gets the current workspace slug
   * @returns string
   */
  public getWorkspaceSlug(): string {
    return this.auth.currentWorkspace?.slug || ''
  }

  /**
   * Get upload history for a specific form
   * @param workspaceSlug Workspace slug
   * @param eventSlug Event slug
   * @param formSlug Form slug
   * @returns Observable with upload history
   */
  public getUploadHistory(workspaceSlug: string, eventSlug: string, formSlug: string): Observable<any> {
    return this.http.get(`${environment.registrationsApi}upload/${workspaceSlug}/${eventSlug}/${formSlug}`).pipe(takeUntil(this._destroy$))
  }

  /**
   * Get status of a specific upload
   * @param uploadId Upload ID
   * @returns Observable with upload status
   */
  public getUploadStatus(uploadId: number): Observable<any> {
    return this.http.get(`${environment.registrationsApi}upload/${uploadId}`).pipe(takeUntil(this._destroy$))
  }

  /**
   * Stop an ongoing upload
   * @param uploadId Upload ID to stop
   * @returns Observable with stop response
   */
  public stopUpload(uploadId: number): Observable<any> {
    return this.http.post(`${environment.registrationsApi}upload/${uploadId}/stop`, {}).pipe(takeUntil(this._destroy$))
  }

  /**
   * Download invalid records from an upload
   * @param uploadId Upload ID
   * @returns Observable with download response as JSON
   */
  public downloadInvalidRecords(uploadId: number): Observable<any> {
    return this.http
      .get(`${environment.registrationsApi}upload/${uploadId}/invalid`, {
        headers: new HttpHeaders({
          Accept: 'application/json'
        })
      })
      .pipe(takeUntil(this._destroy$))
  }

  /**
   * Send invitation emails for an upload
   * @param uploadId Upload ID
   * @returns Observable with send response
   */
  public sendInvitationEmails(uploadId: number): Observable<any> {
    return this.http.post(`${environment.registrationsApi}upload/${uploadId}/send`, {}).pipe(takeUntil(this._destroy$))
  }
  /**
   * Send invitation emails for an upload
   * @param uploadId Upload ID
   * @returns Observable with send response
   */
  public sendConfirmationEmails(uploadId: number, { workspace_slug, event_slug }): Observable<any> {
    return this.http
      .post(`${environment.registrationsApi}upload/${uploadId}/sendConfirmation`, {
        workspace_slug,
        event_slug
      })
      .pipe(takeUntil(this._destroy$))
  }

  /**
   * Upload import data
   * @param workspaceSlug Workspace slug
   * @param eventSlug Event slug
   * @param formSlug Form slug
   * @param payload Upload payload
   * @returns Observable with upload response
   */
  public uploadImportData(workspaceSlug: string, eventSlug: string, formSlug: string, payload: any): Observable<any> {
    const formData = new FormData()
    formData.append('records', JSON.stringify(payload.records))
    formData.append('send_email', (+payload.send_email).toString())
    formData.append('send_whatsapp', (+payload.send_whatsapp).toString())
    formData.append('send_sms', (+payload.send_sms).toString())
    formData.append('force_approval', (+payload.force_approval).toString())
    formData.append('check_email', (+payload.check_email).toString())
    formData.append('send_upload_email', (+payload.send_upload_email).toString())

    return this.http
      .post(`${environment.registrationsApi}upload/${workspaceSlug}/${eventSlug}/${formSlug}`, formData, {
        headers: new HttpHeaders({
          Accept: 'application/json'
        })
      })
      .pipe(takeUntil(this._destroy$))
  }

  updateRegistrationLimit(eventSlug: string, personaSlug: string, registrationId: string, limit: number) {
    return this.http.patch(`${environment.registrationsApi}process/update/max-registration`, {
      event_slug: eventSlug,
      workspace_slug: personaSlug,
      id: registrationId,
      max_registration: limit
    })
  }

  /**
   * Generate filters from natural language query using AI
   * @param query The natural language search query
   * @param formSchema The form schema with field definitions
   * @returns Observable with generated filters
   */
  generateAIFilters(query: string, formSchema: AISearchFormSchema): Observable<AISearchResponse> {
    return this.http
      .post<AISearchResponse>(`${environment.registrationsApi}ai-search/generate-filters`, {
        query,
        formSchema
      })
      .pipe(takeUntil(this._destroy$))
  }
}

export interface ParentRegistrationOption {
  id: string
  name: string
  email: string
  organization: string
  childCount: number
  /** Set when this row is itself a child. Those rows are not valid import parents. */
  parentId: string
}

export interface ParentRegistrationSearch {
  items: ParentRegistrationOption[]
  total: number
  currentPage: number
  lastPage: number
}

export interface GroupMemberImportPayload {
  file: File
  parentId: string
  dataMapping: Record<string, string>
  /** true sends check_email=1. false skips email validation. */
  checkEmail: boolean
}

/**
 * Form schema for AI search
 */
export interface AISearchFormSchema {
  fields: Array<{
    name: string
    label: string
    type: string
    options?: Array<{ slug?: string; name: string }>
  }>
  systemFields?: {
    days?: string[]
  }
}

/**
 * AI search response
 */
export interface AISearchResponse {
  success: boolean
  error: string | null
  filters: {
    fieldFilters: { [key: string]: { values: string[]; inverted?: boolean } }
    searchTerm?: string
    sortField?: string | null
    sortDirection?: 'asc' | 'desc'
  } | null
}
